/**
 * ContainerBackgroundControl — the Container block's Background control with the
 * deck's 3-type selector (Image / GIF · Color · Gradient). Bucket D / T3
 * (Gordon 2026-07-06; deck concepts/edit-wheel-flow/44a-44b).
 *
 * REUSE, NOT DUPLICATION — every sub-surface is the app's existing one:
 *   • Color    → the existing <ColorField> (native color + opacity + reset), the
 *                SAME control the legacy "Background Fill" used.
 *   • Gradient → the app's REAL gradient builder <GradientBuilderPanel> (the same
 *                Linear / Radial / Conic + stops surface used for Wheel/Segment
 *                gradients) — NOT a second hand-rolled gradient UI (deck 44b).
 *   • Image/GIF→ the universal image picker <MediaSelection isGifAllowed> (Upload
 *                / Embed URL / Browse / Recent — the same picker every image
 *                surface uses; deck 44a). The Segment fills panel is the usage
 *                precedent (SegmentFillPanel).
 *
 * The gradient + media pickers are lazy-loaded (heavy, browser-only subtrees), so
 * this control stays importable in lightweight contexts and only loads a picker
 * when that tab is opened — mirroring SegmentFillPanel.
 *
 * Controlled + presentational: reads the ContainerEditor `value` (background /
 * opacity / containerFill) and emits partial patches via `onPatch` (== the
 * editor's patch()). It owns no server state; the chosen fill persists as the
 * `containerFill` craft node prop through the container's existing save path.
 *
 * Tokens: DM Sans / #9B5AF8 / ink #123051. A11y: the type selector is a real ARIA
 * radiogroup; controls are keyboard reachable; motion is CSS-free (reduced-motion
 * safe by construction). Targets match the sibling SegmentStyling panels (44px).
 */

import React, { useMemo } from "react";
import { tokens } from "../../../../theme/tokens";
import { ColorField } from "../../BlockEditorPane/controls";
import {
  CONTAINER_FILL_TYPES,
  CONTAINER_FILL_TYPE_LABEL,
  defaultGradientFill,
  defaultImageFill,
  fillTypeOf,
  resolveContainerBackground,
} from "./containerBackground";

// Lazy — heavy, browser-only picker subtrees (see SegmentFillPanel precedent).
const GradientBuilderPanel = React.lazy(() =>
  import("../../Shared/SegmentStyling/GradientBuilderPanel")
);
const MediaSelection = React.lazy(() => import("../../Shared/MediaSelection"));

const PL = "#9B5AF8";
const INK = "#123051";
const SUB = "#565556";
const LINE = "#e1e1e1";
const PAPER = "#FFFFFF";

const S = {
  wrap: { fontFamily: tokens.fontFamily, color: INK },
  label: {
    fontFamily: tokens.fontFamily,
    fontSize: 12,
    fontWeight: tokens.fontWeight.semibold,
    color: INK,
    display: "block",
    marginBottom: 6,
  },
  tablist: { display: "flex", gap: tokens.spacing.xs, flexWrap: "wrap", marginBottom: tokens.spacing.md },
  tab: (active) => ({
    flex: "0 0 auto",
    border: `1px solid ${active ? PL : LINE}`,
    background: active ? "#F1E9FE" : PAPER,
    color: active ? "#6f27d6" : SUB,
    borderRadius: tokens.radius.md,
    padding: "10px 14px",
    minHeight: 44, // matches sibling SegmentStyling panels (GradientBuilderPanel / SegmentFillPanel)
    font: `600 12.5px ${tokens.fontFamily}`,
    cursor: "pointer",
  }),
  previewWrap: { display: "flex", alignItems: "center", gap: tokens.spacing.md, marginBottom: tokens.spacing.md },
  preview: (bg) => ({
    width: 56,
    height: 56,
    borderRadius: tokens.radius.md,
    border: `1px solid ${LINE}`,
    background: bg,
    flex: "0 0 auto",
  }),
  hint: { fontSize: 11, color: SUB, marginTop: tokens.spacing.sm },
  sub: { marginTop: tokens.spacing.md },
};

const DEFAULT_COLOR = "#F3ECFF";

const extractUrl = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.src || value.url || value.file || value.path || "";
};

/**
 * OPT-IN EXTENSION (deck-15 Title Bar Patterns tab, 2026-07-28) — both props
 * default to nothing, so every existing caller renders BYTE-IDENTICALLY and the
 * Container block's own 3-type selector is untouched:
 *   extraTypes  {Array<{value:string,label:string}>} extra tabs appended after
 *               the canonical three.
 *   renderExtra {(type:string) => ReactNode} the sub-panel for an extra tab.
 * An extra type is selected by handing `onPatch` a fill of that type, which the
 * host owns — this control never invents one.
 */
const ContainerBackgroundControl = ({
  value = {},
  onPatch = () => {},
  extraTypes = [],
  renderExtra = null,
}) => {
  const containerFill = value.containerFill;
  const rawType = fillTypeOf(containerFill);
  const extra = Array.isArray(extraTypes) ? extraTypes : [];
  const extraValues = extra.map((t) => t.value);
  // fillTypeOf() reports "color" for anything it does not model; an extra type
  // is recognised straight off the stored fill so its tab stays selected.
  const currentType =
    containerFill && extraValues.includes(containerFill.type)
      ? containerFill.type
      : rawType;
  const background = value.background || DEFAULT_COLOR;
  const [media, setMedia] = React.useState(null);

  // The live preview swatch = exactly what the container will paint.
  const previewBg = useMemo(() => {
    if (currentType === "color") return background;
    const res = resolveContainerBackground(containerFill);
    return res.mode === "fill" ? res.css : background;
  }, [currentType, containerFill, background]);

  const selectType = (type) => {
    if (type === currentType) return;
    if (extraValues.includes(type)) {
      // The host owns an extra type's fill shape; it seeds one on selection.
      onPatch({ containerFill: { type } });
    } else if (type === "color") {
      // Explicit clear (null, not undefined): the wiring deletes the structured
      // fill prop so the renderer falls back to the legacy solid-color path.
      onPatch({ containerFill: null });
    } else if (type === "gradient") {
      onPatch({
        containerFill:
          containerFill && containerFill.type === "gradient"
            ? containerFill
            : defaultGradientFill(),
      });
    } else {
      onPatch({
        containerFill:
          containerFill && containerFill.type === "image"
            ? containerFill
            : defaultImageFill(),
      });
    }
  };

  const onGradientChange = (gradient) =>
    onPatch({ containerFill: { type: "gradient", gradient } });

  const applyMedia = (_type, val) => {
    const url = extractUrl(val);
    if (!url) return;
    onPatch({
      containerFill: {
        type: "image",
        image: { ...((containerFill && containerFill.image) || {}), url },
      },
    });
  };
  const clearMedia = () => onPatch({ containerFill: { type: "image", image: {} } });

  return (
    <div style={S.wrap} data-container-background>
      <span style={S.label} id="container-bg-label">
        Background
      </span>

      {/* live preview swatch (what the container paints) */}
      <div style={S.previewWrap}>
        <div
          style={S.preview(previewBg)}
          role="img"
          aria-label={`Container background preview: ${
            CONTAINER_FILL_TYPE_LABEL[currentType] ||
            (extra.find((t) => t.value === currentType) || {}).label ||
            currentType
          }`}
          data-container-bg-preview
        />
      </div>

      {/* type selector: real ARIA radiogroup (the canonical 3, plus any opt-in
          extra tabs a host passed — none by default) */}
      <div role="radiogroup" aria-labelledby="container-bg-label" style={S.tablist}>
        {[
          ...CONTAINER_FILL_TYPES.map((t) => ({
            value: t,
            label: CONTAINER_FILL_TYPE_LABEL[t],
          })),
          ...extra,
        ].map((t) => {
          const active = currentType === t.value;
          return (
            <button
              key={t.value}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              style={S.tab(active)}
              onClick={() => selectType(t.value)}
              data-container-bg-type={t.value}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* per-type sub-panel */}
      {currentType === "color" && (
        <ColorField
          label="Background Fill"
          color={background}
          onChange={(c) => onPatch({ background: c })}
          onReset={() => onPatch({ background: DEFAULT_COLOR })}
          slider={{ title: "Opacity", value: value.opacity ?? 100, min: 0, max: 100, unit: "%" }}
          onSlider={(n) => onPatch({ opacity: n })}
        />
      )}

      {currentType === "gradient" && (
        <div style={S.sub} data-subpanel="gradient">
          <React.Suspense fallback={<p style={S.hint}>Loading gradient builder…</p>}>
            <GradientBuilderPanel
              gradient={(containerFill && containerFill.gradient) || undefined}
              onChange={onGradientChange}
            />
          </React.Suspense>
        </div>
      )}

      {renderExtra && extraValues.includes(currentType) && (
        <div style={S.sub} data-subpanel={currentType}>
          {renderExtra(currentType)}
        </div>
      )}

      {currentType === "image" && (
        <div style={S.sub} data-subpanel="image">
          <p style={S.hint}>
            Upload or pick an image or GIF; it fills the container (cover). Fine-tune framing under
            Advanced.
          </p>
          <React.Suspense fallback={<p style={S.hint}>Loading picker…</p>}>
            <MediaSelection
              type="image"
              isGifAllowed
              categories={["Upload", "Embed URL", "Browse", "Recent"]}
              media={media}
              setMedia={setMedia}
              handleChange={applyMedia}
              handleRemove={clearMedia}
              handleTitle={() => {}}
            />
          </React.Suspense>
        </div>
      )}
    </div>
  );
};

export default ContainerBackgroundControl;
